import { Store } from '@ngrx/store';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, throwError, Subject } from 'rxjs';
import { catchError, finalize, map, shareReplay, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { JwtHelperService } from "@auth0/angular-jwt";
import { AppErrors, NotFoundError, UnauthorizedError } from '@fitness-force/models';;
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import type { LANGUAGE, LoginDataModel, LoginPayload, LoginResponse } from '@fitness-force/models';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { environment } from '@fitness-force/environments';

const helper = new JwtHelperService();
@Injectable({
  providedIn: 'root'
})

export class AuthSevices {
  private http = inject(HttpClient);
  private router = inject(Router);
  private store = inject(Store);
  private readonly backEndClientHandler = inject(HttpBackend);
  private injector = inject(Injector);

  public isLoggingOut = false;
  private readonly httpBackend: HttpClient;
  // Subject to communicate logout events to interceptor
  public logoutTrigger = new Subject<void>();
  private refreshInFlight$: Observable<string | null> | null = null;
  private readonly refreshTokenResultSubject = new BehaviorSubject<string | null>(null);
  private refreshFailureCount = 0;
  private readonly maxRefreshFailures = 3;
  private lastRefreshError: unknown = null;

  constructor() {
    const backEndClientHandler = this.backEndClientHandler;
    this.httpBackend = new HttpClient(backEndClientHandler);
  }

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    })
  };

  setTokenInMemory(token: string): void {
    localStorage.setItem('token', token);
  }

  loginService(logindata: LoginDataModel, BASE_URL: string) {
    return this.httpBackend.post(BASE_URL + CONST_URL_SEGEMENTS.AUTH.LOGIN_TOKEN, logindata, this.httpOptions)
      .pipe(
        map((res: any) => {
          if (res?.token) {
            localStorage.setItem('token', res.token);
            if (res.refresh_token) {
              localStorage.setItem('refresh_token', res.refresh_token);
            }
            return res;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          else if (error.status === 401) {
            return throwError(() => new UnauthorizedError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  login(logindata: LoginDataModel): Observable<LoginResponse> {
    this.httpOptions.headers.set('client_id', 'staff-spa');
    return this.httpBackend.post<LoginResponse>(environment.BASE_URL + CONST_URL_SEGEMENTS.AUTH.LOGIN_TOKEN, logindata, this.httpOptions);
  }


  socialLogin(loginData: LoginPayload) {
    this.httpOptions.headers.set('client_id', 'staff-spa');
    return this.httpBackend.post(`${environment.BASE_URL}/external_auth`, loginData, this.httpOptions)
      .pipe(
        map((res: any) => {
          if (res?.token) {
            localStorage.setItem('token', res.token);
            if (res.refresh_token) {
              localStorage.setItem('refresh_token', res.refresh_token);
            }
            return res;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          else if (error.status === 401) {
            return throwError(() => new UnauthorizedError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  sendForgotPasswordEmail(payload: any, BASE_URL: string) {
    return this.http.post(BASE_URL + CONST_URL_SEGEMENTS.AUTH.PASSWORD_LOOKUP, payload, this.httpOptions)
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return false;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  sendUniqueIndetifierEmail(payload: any, BASE_URL: string,) {
    return this.http.post(BASE_URL + CONST_URL_SEGEMENTS.AUTH.UNIQUE_IDENTIFIER ,payload, this.httpOptions)
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return false;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  validateNewpassword(pass: any, userid: number, BASE_URL: string) {
    return this.http.post(BASE_URL + CONST_URL_SEGEMENTS.AUTH.PASSWORD_RESET + userid + '/password',
      pass, this.httpOptions)
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return false;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  logout() {
    // Set logout flag to prevent interceptor from handling auth errors
    this.isLoggingOut = true;
    this.resetRefreshCoalescingState();

    // Trigger logout event for interceptor to cancel all ongoing requests
    this.logoutTrigger.next();

    // Call revoke API before clearing tokens
    this.revokeToken().subscribe({
      next: () => {
        this.clearTokensAndNavigate();
      },
      error: () => {
        // Even if revoke fails, clear tokens and navigate
        this.clearTokensAndNavigate();
      }
    });
  }


  private clearTokensAndNavigate() {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_response');
    this.resetRefreshFailureState();
    this.resetLogoutFlag();
    this.router.navigate(['/auth/login']);
  }

  resetLogoutFlag() {
    this.isLoggingOut = false;
  }

  private resetRefreshCoalescingState(): void {
    this.refreshInFlight$ = null;
    this.refreshTokenResultSubject.next(null);
  }

  getLastRefreshError(): unknown {
    return this.lastRefreshError;
  }

  shouldForceLogoutOnRefreshError(error: unknown = this.lastRefreshError): boolean {
    if (this.refreshFailureCount >= this.maxRefreshFailures) {
      return true;
    }

    if (!error) {
      return false;
    }

    if (error instanceof Error) {
      if (
        error.message === 'No refresh token available' ||
        error.message === 'Invalid refresh token format' ||
        error.message === 'Invalid refresh token response' ||
        error.message === 'Logout in progress'
      ) {
        return true;
      }
    }

    const status = (error as { status?: number })?.status;
    if (status === 400 || status === 401 || status === 403) {
      return true;
    }

    if (status === 0 || status === 502 || status === 503 || status === 504) {
      return false;
    }

    return false;
  }

  private isAuthClassRefreshError(error: unknown): boolean {
    if (error instanceof Error) {
      return (
        error.message === 'No refresh token available' ||
        error.message === 'Invalid refresh token format' ||
        error.message === 'Invalid refresh token response'
      );
    }
    const status = (error as { status?: number })?.status;
    return status === 400 || status === 401 || status === 403;
  }

  private handleRefreshFailure(error: unknown): void {
    this.lastRefreshError = error;
    if (this.isAuthClassRefreshError(error)) {
      this.refreshFailureCount += 1;
    }
  }

  private resetRefreshFailureState(): void {
    this.refreshFailureCount = 0;
    this.lastRefreshError = null;
  }

  private persistTokensFromRefreshResponse(response: { token?: string; refresh_token?: string }): string | null {
    if (this.isLoggingOut) {
      return null;
    }
    if (response?.token) {
      localStorage.setItem('token', response.token);
      if (response.refresh_token) {
        localStorage.setItem('refresh_token', response.refresh_token);
      }
      return response.token;
    }
    return null;
  }

  get tokenFromStorage(): string | null {
    return localStorage.getItem('token');
  }

  get refreshTokenFromStorage(): string | null {
    return localStorage.getItem('refresh_token');
  }

  get decodedToken(): any {
    const getToken: any = this.tokenFromStorage;
    return helper.decodeToken(getToken);
  }

  decodedJwtToken(token: string): any {
    return helper.decodeToken(token);
  }



  get selectedLanguageStorage(): string {
    return localStorage.getItem('DEFAULT_LANGUAGE') as string;
  }

  //Need to take decision for storage clear of language
  get selectedLanguage(): LANGUAGE {
    const selectedLang: LANGUAGE = JSON.parse(this.selectedLanguageStorage);
    if (selectedLang?.LANG_CODE)
      return JSON.parse(this.selectedLanguageStorage);
    else
      return { COUNTRY: 'India', LANG_ABBRE: 'India', 'LANG_CODE': 'en' } as LANGUAGE
  }

  get parseDecodedToken() {
    const buffDecodedToken = this.decodedToken;
    if (buffDecodedToken?.accessed_tenant) {
      const parsedAccessed_tenant = buffDecodedToken.accessed_tenant.split(',');
      const allTenant = buffDecodedToken.all_tenant.split(',');
      buffDecodedToken.accessed_tenant = parsedAccessed_tenant;
      buffDecodedToken.all_tenant = allTenant;
      return buffDecodedToken;
    } else {
      return null
    }
  }



  //this is used to get localized value
  getLocalizedFieldData(primaryData: string, secondaryData: string): Observable<string> {
    return combineLatest([this.store.select(COMPANY_INFO), this.store.select(TENANTS_INFO)]).pipe(
      map(([comp, tent]) => {
        if (comp?.compDetails?.is_secondary_language) {
          if (comp?.selectedLang?.LANG_CODE == tent?.selected_tenant?.secondary_language)
            return secondaryData;
          else
            return primaryData;
        }
        else {
          return primaryData;
        }
      })
    )
  }

  get isAccessTokenValid(): boolean {
    const token = this.tokenFromStorage;
    return !this._isTokenExpired(token, (new Date()).getTimezoneOffset() * 60);
  }

  get isLoginedIn(): boolean {
    return !!(
      this.tokenFromStorage &&
      this.decodedToken &&
      this.isAccessTokenValid
    );
  }

  hasValidSession(): boolean {
    if (this.isLoginedIn) {
      return true;
    }

    const refreshToken = this.refreshTokenFromStorage;
    return !!(
      refreshToken &&
      refreshToken !== 'null' &&
      refreshToken.trim() !== '' &&
      this.decodedToken
    );
  }

  /**
   * Returns true when the access JWT is valid, or after a successful silent refresh.
   * Used by route guards and auto-login before rejecting the session.
   */
  ensureAccessTokenValid(): Observable<boolean> {
    if (this.isLoginedIn) {
      return of(true);
    }

    const refreshToken = this.refreshTokenFromStorage;
    if (!refreshToken || refreshToken === 'null' || refreshToken.trim() === '') {
      return of(false);
    }

    return this.refreshAccessTokenCoalesced().pipe(
      map((token) => !!token && this.isLoginedIn),
      catchError(() => of(false))
    );
  }

  /**
   * Coalesced refresh used by the HTTP interceptor and ensureAccessTokenValid().
   * Set forceRefresh when the API has rejected the current access token, even if
   * its local expiry claim still appears valid.
   * Returns the new access token string, or null on failure.
   */
  refreshAccessTokenCoalesced(forceRefresh = false): Observable<string | null> {
    if (!forceRefresh && this.isLoginedIn) {
      return of(this.tokenFromStorage);
    }

    const storedToken = this.tokenFromStorage;
    if (!forceRefresh && storedToken && this.isAccessTokenValid) {
      return of(storedToken);
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.refreshToken().pipe(
      map((response) => {
        if (this.isLoggingOut) {
          this.refreshTokenResultSubject.next(null);
          return null;
        }
        const token = this.persistTokensFromRefreshResponse(response) ?? this.tokenFromStorage;
        this.resetRefreshFailureState();
        this.refreshTokenResultSubject.next(token);
        return token;
      }),
      catchError((error) => {
        this.handleRefreshFailure(error);
        this.refreshTokenResultSubject.next(null);
        return of(null);
      }),
      takeUntil(this.logoutTrigger),
      finalize(() => {
        this.refreshInFlight$ = null;
        setTimeout(() => this.refreshTokenResultSubject.next(null), 200);
      }),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  get currentUser(): any {
    return this.decodedToken ? this.decodedToken.role : "";
  }

  currentUserDetails(): any {
    return this.decodedToken;
  }

  get loggedInStaffId(): number {
    return parseInt(this.decodedToken.Id);
  }

  get loggedInCompanyId(): number {
    return parseInt(this.decodedToken.companyid);
  }


  private _isTokenExpired(
    token: string | null,
    offsetSeconds?: number
  ): boolean {
    if (!token || token === '') {
      return true;
    }
    const date = helper.getTokenExpirationDate(token);
    offsetSeconds = offsetSeconds || 0;

    if (date === null) {
      return false;
    }
    const currentDate = new Date();
    const localDateModified = currentDate.valueOf() + offsetSeconds;

    return !(date.valueOf() > localDateModified);
  }

  loginWithApple(payload: any, baseUrl: string): Observable<any> {
    return this.http.post(`${baseUrl}/auth/apple`, payload)
      .pipe(
        map((response: any) => {
          if (response.token) {
            localStorage.setItem('token', response.token);
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token);
            }
          }
          return response;
        })
      );
  }

  loginWithMicrosoft(payload: any, baseUrl: string): Observable<any> {
    return this.http.post(`${baseUrl}/auth/microsoft`, payload)
      .pipe(
        map((response: any) => {
          if (response.token) {
            localStorage.setItem('token', response.token);
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token);
            }
          }
          return response;
        })
      );
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.refreshTokenFromStorage;

    if (!refreshToken || refreshToken.trim() === '') {
      return throwError(() => new Error('No refresh token available'));
    }

    if (refreshToken.length < 10) {
      localStorage.removeItem('refresh_token');
      return throwError(() => new Error('Invalid refresh token format'));
    }

    const payload = { refresh_token: refreshToken };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true',
      'client_id': 'staff-spa'
    });

    return this.httpBackend.post(environment.BASE_URL + CONST_URL_SEGEMENTS.AUTH.REFRESH_TOKEN, payload, { headers })
      .pipe(
        map((response: any) => {
          const token = this.persistTokensFromRefreshResponse(response);
          if (token) {
            return response;
          }
          if (this.isLoggingOut) {
            throw new Error('Logout in progress');
          }
          throw new Error('Invalid refresh token response');
        }),
        catchError((error: any) => {
          if (!this.isLoggingOut && (error.status === 400 || error.status === 401 || error.status === 403)) {
            localStorage.removeItem('refresh_token');
          }
          return throwError(() => error);
        }),
        takeUntil(this.logoutTrigger)
      );
  }

  revokeToken(): Observable<any> {
    const refreshToken = this.refreshTokenFromStorage;
    if (!refreshToken || refreshToken === null || refreshToken === 'null' || refreshToken.trim() === '') {
      return of(null);
    }

    const payload = { refresh_token: refreshToken };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true',
      'client_id': 'staff-spa'
    });

    return this.httpBackend.post(environment.BASE_URL + CONST_URL_SEGEMENTS.AUTH.REVOKE_TOKEN, payload, { headers })
      .pipe(
        catchError((error: any) => {
          console.warn('Token revoke failed:', error);
          return of(null);
        })
      );
  }

}
